<script>
/**
 * @component SnomedCodingPanel
 * @description UC-REG-288 — Panel del administrador médico para el esquema de
 *              codificación SNOMED CT de órdenes de imagen (POC, QA):
 *                - estado del esquema (flag del tenant, modo de la org, IA, catálogo);
 *                - "Pendientes de codificar": órdenes que el médico dejó como
 *                  texto libre; el admin elige el concepto → alimenta el cache
 *                  y codifica la orden;
 *                - auditoría de resoluciones (qué sugirió el sistema, qué
 *                  eligió el médico, qué resolvió el admin);
 *                - reindexado de embeddings del catálogo.
 * @example <SnomedCodingPanel />
 */
import snomedCodingService from "@/services/snomed-coding";

export default {
  name: "SnomedCodingPanel",
  data() {
    return {
      status: null,
      isLoading: false,
      activeTab: "pending",
      pending: { docs: [], total: 0 },
      decisions: { docs: [], total: 0 },
      resolving: null, // decisión en edición
      search: "",
      searchResults: [],
      isSearching: false,
      note: "",
      isReindexing: false,
    };
  },

  computed: {
    /** @returns {boolean} el esquema está activo para esta org */
    isActive() {
      return Boolean(
        this.status &&
          this.status.tenantEnabled &&
          this.status.effectiveMode !== "catalog"
      );
    },
    /** @returns {string} explicación del estado para el admin */
    statusText() {
      if (!this.status) return "";
      if (!this.status.tenantEnabled)
        return "El esquema no está habilitado en este ambiente (SNOMED_CODING_ENABLED).";
      if (this.status.mode === "catalog") {
        return "Esta organización usa el selector de catálogo propio (PRESCRIPTION_IMAGES_MODE = catalog).";
      }
      if (this.status.reason === "no_concepts")
        return "No hay conceptos SNOMED cargados: importar el catálogo.";
      if (this.status.reason === "ai_not_configured") {
        return "Modo snomed-ai sin módulo de IA configurado: funciona como búsqueda directa (snomed-search).";
      }
      return `Modo ${this.status.effectiveMode} · ${
        this.status.conceptsCount
      } conceptos · IA ${
        this.status.aiConfigured ? "configurada" : "no configurada"
      }`;
    },
  },

  async created() {
    await this.load();
  },

  methods: {
    /** @description Carga estado + pendientes + auditoría. */
    async load() {
      this.isLoading = true;
      try {
        this.status = await snomedCodingService.getStatus(true);
        if (this.isActive) {
          [this.pending, this.decisions] = await Promise.all([
            snomedCodingService.getPending({ limit: 100 }),
            snomedCodingService.getDecisions({ limit: 50 }),
          ]);
        }
      } catch (e) {
        this.$message({
          type: "error",
          message: "No se pudo cargar el panel SNOMED",
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Abre la resolución de un pendiente y pre-busca con su texto.
     * @param {Object} decision
     */
    async startResolve(decision) {
      this.resolving = decision;
      this.note = "";
      this.search = decision.inputText;
      await this.doSearch();
    },

    /** @description Búsqueda directa de conceptos para resolver. */
    async doSearch() {
      const q = (this.search || "").trim();
      if (!q) {
        this.searchResults = [];
        return;
      }
      this.isSearching = true;
      try {
        this.searchResults = await snomedCodingService.searchConcepts(q, 15);
      } catch (e) {
        this.searchResults = [];
      } finally {
        this.isSearching = false;
      }
    },

    /**
     * @description Fija el concepto elegido para el pendiente en edición.
     * @param {Object} concept - { sctid, display }
     */
    async resolveWith(concept) {
      if (!this.resolving || !concept) return;
      try {
        await snomedCodingService.resolvePending(this.resolving._id, {
          sctid: concept.sctid,
          note: this.note,
        });
        this.$message({
          type: "success",
          message: `Codificado como ${concept.display}`,
        });
        this.resolving = null;
        await this.load();
      } catch (e) {
        this.$message({
          type: "error",
          message: "No se pudo resolver el pendiente",
        });
      }
    },

    /** @description Recalcula embeddings de conceptos sin vector. */
    async reindex() {
      this.isReindexing = true;
      try {
        const r = await snomedCodingService.reindex(false);
        this.$message({
          type: "success",
          message: `Indexados ${r.indexed} de ${r.total} (fallidos ${r.failed})`,
        });
        await this.load();
      } catch (e) {
        this.$message({
          type: "error",
          message: "No se pudo reindexar (¿hay API key OpenAI cargada?)",
        });
      } finally {
        this.isReindexing = false;
      }
    },

    /**
     * @description Etiqueta de lo que pasó con una decisión (auditoría).
     * @param {Object} d
     * @returns {string}
     */
    outcomeOf(d) {
      if (d.adminResolution && d.adminResolution.sctid)
        return `Admin → ${d.adminResolution.display}`;
      if (d.doctorChoice && d.doctorChoice.status === "coded") {
        return `Médico → ${d.doctorChoice.display}${
          d.doctorChoice.acceptedSuggestion
            ? " (aceptó sugerencia)"
            : " (eligió otra)"
        }`;
      }
      if (d.doctorChoice && d.doctorChoice.status === "free_text")
        return "Médico → texto libre (pendiente)";
      return "Sin elección registrada";
    },

    /**
     * @param {Object} d
     * @returns {string}
     */
    suggestionOf(d) {
      if (!d.suggestion || !d.suggestion.sctid) return "—";
      const pct =
        typeof d.suggestion.confidence === "number"
          ? ` (${Math.round(d.suggestion.confidence * 100)}%)`
          : "";
      return `${d.suggestion.display}${pct} · ${d.layer}`;
    },

    /**
     * @param {string} iso
     * @returns {string}
     */
    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return isNaN(d) ? "" : d.toLocaleString("es-AR");
    },
  },
};
</script>

<template lang="pug">
.snomed-panel
  .snomed-panel__status
    p {{ statusText }}
    el-button(v-if="isActive" size="small" :loading="isReindexing" @click="reindex") Reindexar embeddings
    el-button(size="small" @click="load" :loading="isLoading") Actualizar

  template(v-if="isActive")
    el-tabs(v-model="activeTab")
      el-tab-pane(name="pending")
        span(slot="label") Pendientes de codificar ({{ pending.total }})
        p.snomed-panel__empty(v-if="!pending.docs.length") No hay órdenes pendientes de codificar.
        el-table(v-else :data="pending.docs" size="small")
          el-table-column(label="Fecha" width="160")
            template(slot-scope="{ row }") {{ formatDate(row.createdAt) }}
          el-table-column(label="Texto del médico" prop="inputText")
          el-table-column(label="Médico" width="180")
            template(slot-scope="{ row }") {{ row.user ? (row.user.firstName + ' ' + row.user.lastName) : '' }}
          el-table-column(label="Sugerencia del sistema")
            template(slot-scope="{ row }") {{ suggestionOf(row) }}
          el-table-column(width="120")
            template(slot-scope="{ row }")
              el-button(size="mini" type="primary" @click="startResolve(row)") Codificar

        .snomed-panel__resolve(v-if="resolving")
          h4 Codificar: "{{ resolving.inputText }}"
          .snomed-panel__row
            el-input(v-model="search" size="small" placeholder="Buscar concepto SNOMED" @keyup.enter.native="doSearch" clearable)
            el-button(size="small" :loading="isSearching" @click="doSearch") Buscar
            el-button(size="small" type="text" @click="resolving = null") Cancelar
          el-input(v-model="note" size="small" placeholder="Nota (opcional)" style="margin-top: 8px")
          ul.snomed-panel__list
            li(v-for="c in searchResults" :key="c.sctid")
              button.snomed-panel__option(type="button" @click="resolveWith(c)")
                span {{ c.display }}
                span.snomed-panel__sctid  · {{ c.sctid }} · {{ c.fsn }}
            li.snomed-panel__empty(v-if="!searchResults.length && !isSearching") Sin resultados

      el-tab-pane(name="audit")
        span(slot="label") Auditoría ({{ decisions.total }})
        el-table(:data="decisions.docs" size="small")
          el-table-column(label="Fecha" width="160")
            template(slot-scope="{ row }") {{ formatDate(row.createdAt) }}
          el-table-column(label="Texto" prop="inputText")
          el-table-column(label="Modo" prop="mode" width="120")
          el-table-column(label="Sugerencia (capa)")
            template(slot-scope="{ row }") {{ suggestionOf(row) }}
          el-table-column(label="Resultado")
            template(slot-scope="{ row }") {{ outcomeOf(row) }}
</template>

<style scoped lang="scss">
.snomed-panel {
  &__status {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    p {
      margin: 0;
      flex: 1;
      color: #2d3436;
    }
  }
  &__empty {
    color: #636e72;
    font-size: 13px;
  }
  &__resolve {
    margin-top: 16px;
    padding: 12px;
    border: 1px solid #dfe6e9;
    border-radius: 8px;
    h4 {
      margin: 0 0 8px;
    }
  }
  &__row {
    display: flex;
    gap: 8px;
    align-items: center;
    .el-input {
      flex: 1;
    }
  }
  &__list {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
    max-height: 260px;
    overflow: auto;
  }
  &__option {
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 6px 4px;
    cursor: pointer;
    border-radius: 4px;
    &:hover {
      background: #eef3f7;
    }
  }
  &__sctid {
    color: #636e72;
    font-size: 12px;
  }
}
</style>
